import React, { useMemo } from "react"

import { createSimulationWrapperInstance } from "@/test-set/simulation-wrappers"
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
} from "@coval/ui"
import {
  Braces,
  ChevronDown,
  Columns3,
  EyeOff,
  FileText,
  FlaskConical,
  Info,
  List,
  Type,
} from "lucide-react"

import { OBJECT_VIEW_MODE, SIMULATION_INPUT_VIEW_MODE } from "@/lib/viewModes"

import { useTestSetEditorContext } from "../../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../../event-bus"
import { AddColumnMenu } from "./AddColumnMenu"
import { MetadataColumnHeaders } from "./MetadataColumnHeaders"
import type { ToggleableColumn } from "./TestCaseColumnVisibility"
import type { ColumnWidths } from "./useResizableColumns"

const MIN_COLLAPSED_WIDTH = "480px"

interface TestCaseTableHeaderProps {
  columnWidths: ColumnWidths
}

export const TestCaseTableHeader: React.FC<TestCaseTableHeaderProps> = ({
  columnWidths,
}) => {
  const dispatch = useEventDispatcher()
  const {
    metadataViewMode,
    setMetadataViewMode,
    simulationInputViewMode,
    setSimulationInputViewMode,
    metadata,
    selectableTestCases,
    selectedTestCaseId,
    selectedTestCaseIds,
    requestExpectedBehaviorTest,
    visibleColumns,
    toggleColumn,
  } = useTestSetEditorContext()

  // Reflect selection over every row the table renders, filtered rows plus staged additions, so the header checkbox stays in sync with select-all, which targets that same list.
  const allSelected =
    selectableTestCases.length > 0 &&
    selectableTestCases.every((testCase) =>
      selectedTestCaseIds.has(testCase.id),
    )
  const someSelected = selectedTestCaseIds.size > 0 && !allSelected

  const testableTestCases = useMemo(
    () =>
      selectableTestCases.filter(
        (testCase) =>
          !testCase.id.startsWith("new-") &&
          testCase.expected_behaviors.length > 0,
      ),
    [selectableTestCases],
  )
  const testCaseToOpen =
    testableTestCases.find(({ id }) => id === selectedTestCaseId) ??
    testableTestCases[0]

  const handleSelectAll = () => {
    if (allSelected) {
      dispatch(EVENTS.TEST_CASE_CLEAR_SELECTION, {})
    } else {
      dispatch(EVENTS.TEST_CASE_SELECT_ALL, {})
    }
  }

  const simulationWrapper = useMemo(() => {
    if (!metadata.simulationWrapperType) {
      return null
    }
    return createSimulationWrapperInstance(metadata.simulationWrapperType)
  }, [metadata.simulationWrapperType])

  const hasTestCaseCell = useMemo(() => {
    if (!simulationWrapper) {
      return false
    }
    const testCell = simulationWrapper.renderTestCaseCell(
      {},
      () => {},
      false,
      "200px",
    )
    return testCell !== null
  }, [simulationWrapper])

  const renderHeader = (
    column: keyof ColumnWidths,
    label: string,
    options?: {
      hideKey?: ToggleableColumn
      padding?: string
      typeHint?: string
      hideChevron?: boolean
      center?: boolean
      docHref?: string
      docTooltip?: string
      action?: React.ReactNode
    },
  ) => {
    const {
      hideKey,
      padding = "px-2",
      typeHint,
      hideChevron,
      center,
      docHref,
      docTooltip,
      action,
    } = options || {}
    const docLink = docHref ? (
      <Tooltip
        content={
          <a
            href={docHref}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-coval-primary"
          >
            {docTooltip ?? "Learn more"}
          </a>
        }
      >
        <a
          href={docHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label={docTooltip ?? `Learn more about ${label}`}
          className="text-coval-text-muted hover:text-coval-text"
        >
          <Info className="w-3.5 h-3.5" />
        </a>
      </Tooltip>
    ) : null
    const width = columnWidths[column]
    const isFixedWidth = width.endsWith("px")

    if (hideKey) {
      return (
        <DropdownMenu key={column}>
          <DropdownMenuTrigger asChild>
            <th
              className={`relative py-3 ${center ? "text-center" : "text-left"} text-sm ${padding} font-medium text-coval-text !bg-surface-default cursor-pointer select-none transition-colors hover:bg-coval-contrast/10 data-pressed:opacity-100 aria-expanded:opacity-100`}
              style={{
                width,
                ...(isFixedWidth ? { maxWidth: width, minWidth: width } : {}),
                verticalAlign: "middle",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {hideChevron && !typeHint && !docLink && !action ? (
                label
              ) : (
                <span className="flex items-center justify-between">
                  <span className="flex items-center gap-3">
                    {label}
                    {docLink}
                    {typeHint && (
                      <span className="text-coval-text-muted font-normal truncate">
                        {typeHint}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    {action}
                    {!hideChevron && (
                      <ChevronDown className="w-4 h-4 text-coval-text-muted" />
                    )}
                  </span>
                </span>
              )}
            </th>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              onSelect={() => toggleColumn(hideKey)}
              className="gap-2"
            >
              <EyeOff className="w-4 h-4" />
              Hide column
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }

    return (
      <th
        key={column}
        className={`relative py-3 text-left text-sm ${padding} font-medium text-coval-text !bg-surface-default select-none transition-colors hover:bg-coval-contrast/10`}
        style={{
          width,
          ...(isFixedWidth ? { maxWidth: width, minWidth: width } : {}),
          verticalAlign: "middle",
        }}
      >
        <span className="flex items-center gap-3">
          {label}
          {typeHint && (
            <span className="text-coval-text-muted font-normal truncate">
              {typeHint}
            </span>
          )}
        </span>
      </th>
    )
  }

  return (
    <thead className="!bg-surface-default">
      <tr>
        <th
          className="relative py-3 text-left text-sm px-1.5 font-medium text-coval-text !bg-surface-default transition-colors hover:bg-coval-contrast/10 cursor-pointer"
          style={{
            width: columnWidths.number,
            verticalAlign: "middle",
          }}
          onClick={handleSelectAll}
        >
          <div className="flex items-center justify-center select-all-checkbox">
            <Checkbox
              checked={someSelected ? "indeterminate" : allSelected}
              className="pointer-events-none border-neutral-400"
              aria-label="Select all test cases"
            />
          </div>
        </th>
        {visibleColumns.has("id") &&
          renderHeader("id", "Test Case ID", {
            hideKey: "id",
          })}
        {visibleColumns.has("type") &&
          renderHeader("type", "Type", {
            hideKey: "type",
            hideChevron: true,
          })}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <th
              className="relative py-3 text-left text-sm px-2 font-medium text-coval-text !bg-surface-default cursor-pointer transition-colors hover:bg-coval-contrast/10 data-pressed:opacity-100 aria-expanded:opacity-100"
              style={{
                width: columnWidths.description,
                verticalAlign: "middle",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="flex items-center justify-between">
                <span className="flex items-center gap-3">
                  Simulation Input
                  <span className="text-coval-text-muted font-normal truncate">
                    {simulationInputViewMode ===
                    SIMULATION_INPUT_VIEW_MODE.MARKDOWN
                      ? SIMULATION_INPUT_VIEW_MODE.MARKDOWN
                      : SIMULATION_INPUT_VIEW_MODE.TEXT}
                  </span>
                </span>
                <ChevronDown className="w-4 h-4 text-coval-text-muted" />
              </span>
            </th>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>View as</DropdownMenuLabel>
            <DropdownMenuItem
              onSelect={() =>
                setSimulationInputViewMode(SIMULATION_INPUT_VIEW_MODE.TEXT)
              }
              className={`gap-2 ${
                simulationInputViewMode === SIMULATION_INPUT_VIEW_MODE.TEXT
                  ? "text-coval-contrast"
                  : ""
              }`}
            >
              <Type className="w-4 h-4" />
              Text
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                setSimulationInputViewMode(SIMULATION_INPUT_VIEW_MODE.MARKDOWN)
              }
              className={`gap-2 ${
                simulationInputViewMode === SIMULATION_INPUT_VIEW_MODE.MARKDOWN
                  ? "text-coval-contrast"
                  : ""
              }`}
            >
              <FileText className="w-4 h-4" />
              Markdown
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {visibleColumns.has("description") &&
          renderHeader("testCaseDescription", "Description", {
            hideKey: "description",
            typeHint: "text",
          })}
        {metadataViewMode === OBJECT_VIEW_MODE.JSON &&
        (visibleColumns.has("behaviors") ||
          visibleColumns.has("metadata") ||
          visibleColumns.has("kbEntries")) ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <th
                className="relative py-3 text-left text-sm px-2 font-medium text-coval-text !bg-surface-default cursor-pointer transition-colors hover:bg-coval-contrast/10 data-pressed:opacity-100 aria-expanded:opacity-100"
                style={{
                  width: MIN_COLLAPSED_WIDTH,
                  minWidth: MIN_COLLAPSED_WIDTH,
                  verticalAlign: "middle",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="flex items-center justify-between">
                  <span className="flex items-center gap-3">
                    Data
                    <span className="text-coval-text-muted font-normal truncate">
                      collapsed
                    </span>
                  </span>
                  <ChevronDown className="w-4 h-4 text-coval-text-muted" />
                </span>
              </th>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>View as</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => setMetadataViewMode(OBJECT_VIEW_MODE.COLUMNS)}
                className="gap-2"
              >
                <Columns3 className="w-4 h-4" />
                Columns
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setMetadataViewMode(OBJECT_VIEW_MODE.KEY_VALUE)}
                className="gap-2"
              >
                <List className="w-4 h-4" />
                List
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setMetadataViewMode(OBJECT_VIEW_MODE.JSON)}
                className="gap-2 text-coval-contrast"
              >
                <Braces className="w-4 h-4" />
                Collapsed
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {metadataViewMode !== OBJECT_VIEW_MODE.JSON &&
          visibleColumns.has("behaviors") &&
          renderHeader("behaviors", "Expected Behaviors", {
            hideKey: "behaviors",
            typeHint: "list",
            docHref: "https://docs.coval.ai/concepts/test-sets/evaluation",
            docTooltip: "Learn more about expected behaviors",
            action: (
              <Button
                type="button"
                variant="secondary"
                className="h-7 px-2 text-xs"
                disabled={!testCaseToOpen}
                aria-label="Test expected behaviors"
                onPointerDown={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  if (testCaseToOpen) {
                    requestExpectedBehaviorTest(testCaseToOpen.id)
                  }
                }}
              >
                <FlaskConical className="h-3.5 w-3.5" />
                Test
              </Button>
            ),
          })}
        {metadataViewMode !== OBJECT_VIEW_MODE.JSON &&
          visibleColumns.has("metadata") &&
          (metadataViewMode === OBJECT_VIEW_MODE.COLUMNS ? (
            <MetadataColumnHeaders />
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <th
                  className="relative py-3 text-left text-sm px-2 font-medium text-coval-text !bg-surface-default cursor-pointer transition-colors hover:bg-coval-contrast/10 data-pressed:opacity-100 aria-expanded:opacity-100"
                  style={{
                    width: columnWidths.metadata,
                    verticalAlign: "middle",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="flex items-center justify-between">
                    <span className="flex items-center gap-3">
                      Metadata{" "}
                      <span className="text-coval-text-muted font-normal truncate">
                        list
                      </span>
                    </span>
                    <ChevronDown className="w-4 h-4 text-coval-text-muted" />
                  </span>
                </th>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>View as</DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={() => setMetadataViewMode(OBJECT_VIEW_MODE.COLUMNS)}
                  className={`gap-2 ${
                    (metadataViewMode as string) === OBJECT_VIEW_MODE.COLUMNS
                      ? "text-coval-contrast"
                      : ""
                  }`}
                >
                  <Columns3 className="w-4 h-4" />
                  Columns
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    setMetadataViewMode(OBJECT_VIEW_MODE.KEY_VALUE)
                  }
                  className={`gap-2 ${
                    metadataViewMode === OBJECT_VIEW_MODE.KEY_VALUE
                      ? "text-coval-contrast"
                      : ""
                  }`}
                >
                  <List className="w-4 h-4" />
                  List
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setMetadataViewMode(OBJECT_VIEW_MODE.JSON)}
                  className="gap-2"
                >
                  <Braces className="w-4 h-4" />
                  Collapsed
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => toggleColumn("metadata")}
                  className="gap-2"
                >
                  <EyeOff className="w-4 h-4" />
                  Hide column
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ))}
        {metadataViewMode !== OBJECT_VIEW_MODE.JSON &&
          visibleColumns.has("kbEntries") &&
          renderHeader("kbEntries", "Knowledge Base Entries", {
            hideKey: "kbEntries",
            typeHint: "list",
          })}
        {hasTestCaseCell &&
          renderHeader("simulationMetadata", "Wrapper Metadata")}

        {columnWidths.addColumn !== "0px" && (
          <AddColumnMenu width={columnWidths.addColumn} />
        )}
      </tr>
    </thead>
  )
}
